import type { Colaborador } from '@/lib/supabase/tipos-bd'
import { CeldaDoble, type ColumnaTabla } from '@/shared/ui/Tabla/Tabla'
import { fecha } from '@/shared/utilidades/formato'

import { useOpcionesZonas } from '@/shared/datos/useOpciones'

import { Aviso } from '@/shared/ui/Aviso/Aviso'

import { Pastilla } from '@/shared/ui/Pastilla/Pastilla'

import { PaginaMaestro } from '../compartido/PaginaMaestro'
import { useMaestro } from '../compartido/useMaestro'
import type { CampoMaestro, RegistroMaestro } from '../compartido/tipos'

import { ROL_EN_LA_APP } from './rolEnLaApp'
import { UsuariosDeOficina } from './UsuariosDeOficina'

/** El usuario con el que entra viene de perfiles: es de la sesión, no de la nómina. */
type FilaColaborador = Colaborador &
  RegistroMaestro & {
    perfil: { usuario: string | null; rol: string | null } | null
  }

/** Las columnas de la nómina más el usuario de quien ya puede entrar. */
const SELECCION = '*, perfil:perfiles(usuario, rol)'

const COLUMNAS: ColumnaTabla<FilaColaborador>[] = [
  {
    encabezado: '',
    // Con la columna del rol el nombre se partía en cuatro renglones.
    ancho: '240px',
    celda: (colaborador) => (
      <CeldaDoble principal={colaborador.nombre} secundario={`CC ${colaborador.documento}`} />
    ),
  },
  {
    encabezado: 'Cargo',
    celda: (colaborador) => colaborador.cargo ?? '—',
  },
  {
    // EL ROL EN LA APLICACIÓN, que no es el cargo. Johan preguntó por qué no
    // se veían los roles actualizados: aquí solo estaba el cargo de nómina, y
    // «Proyectos» no dice que esa persona aprueba compras y da horas.
    encabezado: 'Rol en la app',
    ancho: '160px',
    celda: (colaborador) => {
      const rol = colaborador.perfil?.rol ? ROL_EN_LA_APP[colaborador.perfil.rol] : null
      return rol ? (
        <Pastilla tono={rol.tono}>{rol.etiqueta}</Pastilla>
      ) : (
        <span className="apagado">—</span>
      )
    },
  },
  {
    encabezado: 'Teléfono',
    celda: (colaborador) => colaborador.telefono ?? '—',
  },
  {
    // El RETIE exige matrícula para certificar una instalación eléctrica, así
    // que esta columna decide quién puede firmar un acta. Solo se pinta donde
    // significa algo: a quien está en Proyectos o en coordinación no le hace
    // falta, y un «No» ahí se leería como una carencia que no lo es.
    encabezado: 'CONTE',
    ancho: '96px',
    celda: (colaborador) =>
      colaborador.tarjeta_conte === null ? (
        <span className="apagado">—</span>
      ) : colaborador.tarjeta_conte ? (
        <span className="cifra">Vigente</span>
      ) : (
        <span className="apagado">Sin tarjeta</span>
      ),
  },
  {
    encabezado: 'Ingreso',
    ancho: '130px',
    celda: (colaborador) => fecha(colaborador.fecha_ingreso),
  },
  {
    encabezado: 'Entra con',
    ancho: '170px',
    // Lo que se muestra es LO QUE LA PERSONA TECLEA. Un «Sí» no sirve de
    // nada cuando alguien llama diciendo que no puede entrar: lo primero que
    // hay que mirar es con qué usuario lo está intentando.
    celda: (colaborador) =>
      colaborador.perfil?.usuario ? (
        <span className="codigo">{colaborador.perfil.usuario}</span>
      ) : (
        <span className="apagado">Todavía no puede entrar</span>
      ),
  },
  {
    // El correo ya no es la llave de nada: es por dónde se le manda algo. Va
    // en su propia columna y su ausencia ya no impide entrar.
    encabezado: 'Correo',
    celda: (colaborador) => colaborador.correo ?? <span className="apagado">—</span>,
  },
]

/**
 * Personal de campo.
 *
 * POR QUÉ ESTO NO ES LO MISMO QUE UN USUARIO, aunque lo parezca.
 *
 * Un colaborador acumula órdenes, horas y retrasos desde el primer día. Si
 * mañana se va de la empresa y alguien borra su usuario de la aplicación, ese
 * historial NO PUEDE IRSE CON ÉL: las órdenes de trabajo apuntan al
 * colaborador, no al usuario, justo para eso. Además tiene datos que un
 * usuario no tiene —cédula, cargo, fecha de ingreso, zona habitual—.
 *
 * Lo que sí sobraba eran DOS PANTALLAS: la persona se creaba aquí y su usuario
 * en otra parte, y quedaba el vínculo colgando sin que nadie lo viera. Ahora
 * la columna dice si puede entrar a la aplicación, que es la consecuencia
 * práctica de tener usuario o no tenerlo.
 */
export function PaginaColaboradores() {
  const { opciones: zonas } = useOpcionesZonas()
  // La misma consulta que ya hace PaginaMaestro por dentro: react-query la
  // comparte, así que esto no pide nada de más al servidor.
  const { registros } = useMaestro<FilaColaborador>({
    tabla: 'colaboradores',
    ordenarPor: 'nombre',
    seleccion: SELECCION,
  })

  const total = registros.length
  const sinUsuario = registros.filter((c) => !c.perfil?.usuario).length

  const campos: CampoMaestro[] = [
    { nombre: 'documento', etiqueta: 'Cédula', tipo: 'texto', obligatorio: true, medio: true },
    {
      nombre: 'nombre',
      etiqueta: 'Nombre completo',
      tipo: 'texto',
      obligatorio: true,
      medio: true,
    },
    { nombre: 'cargo', etiqueta: 'Cargo', tipo: 'texto', medio: true },
    { nombre: 'telefono', etiqueta: 'Teléfono', tipo: 'telefono', medio: true },
    {
      nombre: 'correo',
      etiqueta: 'Correo',
      tipo: 'texto',
      medio: true,
      ayuda: 'Solo para contacto. A la aplicación se entra con usuario, no con correo.',
    },
    {
      nombre: 'zona_principal_id',
      etiqueta: 'Zona principal',
      tipo: 'selector',
      opciones: zonas,
      medio: true,
      ayuda: 'Dónde trabaja habitualmente.',
    },
    { nombre: 'fecha_ingreso', etiqueta: 'Fecha de ingreso', tipo: 'fecha', medio: true },
  ]

  // Quién ya tiene ficha de colaborador: el resto de usuarios va aparte.
  const conFicha = new Set(
    registros.map((c) => c.perfil_id).filter((id): id is string => Boolean(id))
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--e-5)' }}>
      <PaginaMaestro<FilaColaborador>
        titulo="Colaboradores"
        subtitulo="Quiénes ejecutan el trabajo en campo"
        nota={
          sinUsuario > 0 ? (
            <Aviso
              tipo="alerta"
              titulo={`${sinUsuario} de ${total} no pueden entrar a la aplicación`}
            >
              Sin usuario no marcan llegada, no reciben avisos en el teléfono y no les llega ninguna
              orden.
            </Aviso>
          ) : null
        }
        tabla="colaboradores"
        ordenarPor="nombre"
        seleccion={SELECCION}
        singular="colaborador"
        columnas={COLUMNAS}
        campos={campos}
        textoDeBusqueda={(colaborador) =>
          [
            colaborador.nombre,
            colaborador.documento,
            colaborador.cargo,
            colaborador.perfil?.usuario,
          ]
            .filter(Boolean)
            .join(' ')
        }
      />

      {/* Camilo y el resto de gerencia entran a la app pero no salen a campo:
        no estaban en ninguna parte de esta pantalla. */}
      <UsuariosDeOficina conFicha={conFicha} />
    </div>
  )
}
